/**
 * Public, non-authenticated documentation page for the tenant Products API
 * (`GET/POST /api/public/v1/products`, `GET/PATCH/DELETE /products/{id}`,
 * `POST /products/{id}/restore`).
 *
 * Renders a friendly view over the real `GET /api/public/v1/openapi.json`
 * document (the backend's hand-curated OpenAPI 3.1 spec — see
 * `backend/src/features/public-api/openapi/spec.ts`) rather than duplicating
 * endpoint descriptions by hand, so this page can't drift from the actual
 * API surface. It deliberately shows only the `Products`-tagged paths —
 * Inventory and Sync are real endpoints on the same public API, but out of
 * scope for this page by design (see the Inventory Settings "Public API
 * Access" card this page is linked from).
 */
import { useMemo } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { AlertTriangle, KeyRound, RefreshCw } from 'lucide-react'
import { AuthShell } from '@/components/layout/AuthShell'
import { Skeleton } from '@/components/ui/skeleton'

// ---------------------------------------------------------------------------
// Minimal local typing over the OpenAPI 3.1 document — just enough shape to
// render it. The document itself is the source of truth; these types exist
// only to make traversal safe, not to model the full OpenAPI spec.
// ---------------------------------------------------------------------------

interface OaSchemaProperty {
  type?: string
  format?: string
  description?: string
  nullable?: boolean
  enum?: string[]
  pattern?: string
  maxLength?: number
  minLength?: number
  minimum?: number
  $ref?: string
}

interface OaSchema {
  type?: string
  description?: string
  required?: string[]
  properties?: Record<string, OaSchemaProperty>
  enum?: string[]
}

interface OaParameter {
  in: string
  name: string
  description?: string
  required?: boolean
  schema?: OaSchemaProperty
}

interface OaResponse {
  description?: string
}

interface OaOperation {
  summary?: string
  description?: string
  tags?: string[]
  parameters?: Array<OaParameter | { $ref: string }>
  requestBody?: {
    required?: boolean
    content?: Record<string, { schema?: { $ref?: string } }>
  }
  responses?: Record<string, OaResponse>
  security?: Array<Record<string, string[]>>
}

type OaPathItem = Record<string, unknown>

interface OpenApiDoc {
  info: { title: string; version: string; description?: string }
  servers?: Array<{ url: string; description?: string }>
  components: {
    schemas: Record<string, OaSchema>
    headers?: Record<string, { description?: string; schema?: OaSchemaProperty }>
  }
  paths: Record<string, OaPathItem>
}

const HTTP_METHODS = ['get', 'post', 'patch', 'put', 'delete'] as const

interface Endpoint {
  method: string
  path: string
  summary?: string | undefined
  description?: string | undefined
  parameters: OaParameter[]
  requestBodySchemaName?: string | undefined
  responses: Array<{ code: string; description?: string | undefined }>
  scopes: string[]
}

function isParameter(p: OaParameter | { $ref: string }): p is OaParameter {
  return 'name' in p
}

function schemaRefName(ref: string | undefined): string | undefined {
  return ref?.split('/').pop()
}

/** Only the Products-tagged surface — Inventory and Sync are intentionally excluded. */
function collectProductEndpoints(doc: OpenApiDoc): Endpoint[] {
  const endpoints: Endpoint[] = []
  for (const [path, item] of Object.entries(doc.paths)) {
    if (!path.startsWith('/products')) continue
    const sharedParams = Array.isArray(item['parameters'])
      ? (item['parameters'] as Array<OaParameter | { $ref: string }>).filter(isParameter)
      : []
    for (const method of HTTP_METHODS) {
      const op = item[method] as OaOperation | undefined
      if (!op) continue
      const ownParams = (op.parameters ?? []).filter(isParameter)
      const bodyRef = op.requestBody?.content?.['application/json']?.schema?.$ref
      const scopes = Array.from(new Set((op.security ?? []).flatMap((s) => Object.values(s).flat())))
      endpoints.push({
        method: method.toUpperCase(),
        path,
        summary: op.summary,
        description: op.description,
        parameters: [...sharedParams, ...ownParams],
        requestBodySchemaName: schemaRefName(bodyRef),
        responses: Object.entries(op.responses ?? {}).map(([code, r]) => ({
          code,
          description: r.description,
        })),
        scopes,
      })
    }
  }
  // Stable, readable order: list → get-by-id → mutate → delete → restore.
  const order = ['/products', '/products/{id}', '/products/{id}/restore']
  return endpoints.sort((a, b) => order.indexOf(a.path) - order.indexOf(b.path))
}

function describeProperty(
  doc: OpenApiDoc,
  prop: OaSchemaProperty,
): { type: string; notes?: string | undefined } {
  if (prop.$ref) {
    const resolved = doc.components.schemas[schemaRefName(prop.$ref) ?? '']
    if (resolved?.enum) return { type: 'string', notes: `one of: ${resolved.enum.join(', ')}` }
    return { type: schemaRefName(prop.$ref) ?? 'object' }
  }
  const type = prop.nullable ? `${prop.type ?? 'any'} | null` : prop.type ?? 'any'
  const notes = [
    prop.format,
    prop.enum ? `one of: ${prop.enum.join(', ')}` : undefined,
    prop.pattern ? `pattern ${prop.pattern}` : undefined,
    typeof prop.maxLength === 'number' ? `max ${prop.maxLength} chars` : undefined,
    typeof prop.minimum === 'number' ? `min ${prop.minimum}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ')
  return { type, notes: notes || undefined }
}

function useProductApiSpec(): UseQueryResult<OpenApiDoc, Error> {
  return useQuery({
    queryKey: ['public-product-api-openapi'],
    queryFn: async (): Promise<OpenApiDoc> => {
      const res = await fetch('/api/public/v1/openapi.json')
      if (!res.ok) throw new Error(`Failed to load API specification (${res.status})`)
      return (await res.json()) as OpenApiDoc
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

export function PublicProductApiDocsPage(): JSX.Element {
  const spec = useProductApiSpec()

  return (
    <AuthShell align="start">
      <div className="w-full max-w-4xl mx-auto space-y-6">
        <header className="space-y-2">
          <div className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-brand/10 text-brand-secondary text-[11px] font-semibold uppercase tracking-wide">
            Public API · Products
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Products API documentation</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Manage your tenant&apos;s products programmatically. This page covers the{' '}
            <span className="font-medium text-foreground">Products</span> endpoints only —
            Inventory and Sync are separate, internal-facing parts of the same public API and
            aren&apos;t documented here.
          </p>
        </header>

        {spec.isLoading && <DocsSkeleton />}

        {spec.isError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              Couldn&apos;t load the API specification right now. Try refreshing the page, or fetch
              it directly from{' '}
              <code className="bg-destructive/10 px-1 rounded">/api/public/v1/openapi.json</code>.
            </div>
          </div>
        )}

        {spec.data && <ProductApiDocs doc={spec.data} />}
      </div>
    </AuthShell>
  )
}

function DocsSkeleton(): JSX.Element {
  return (
    <div className="space-y-4" role="status" aria-busy="true" aria-live="polite" aria-label="Loading API documentation">
      <Skeleton className="h-24 w-full rounded-lg" />
      <Skeleton className="h-40 w-full rounded-lg" />
      <Skeleton className="h-40 w-full rounded-lg" />
      <Skeleton className="h-40 w-full rounded-lg" />
    </div>
  )
}

function ProductApiDocs({ doc }: { doc: OpenApiDoc }): JSX.Element {
  const endpoints = useMemo(() => collectProductEndpoints(doc), [doc])
  const baseUrl = doc.servers?.[0]?.url ?? '/api/public/v1'
  const productSchema = doc.components.schemas['Product']
  const problemSchema = doc.components.schemas['Problem']

  return (
    <div className="space-y-6">
      {/* ── Authentication ──────────────────────────────────────────── */}
      <section className="rounded-lg border border-border bg-card p-4 sm:p-5 space-y-3">
        <h2 className="text-[15px] font-semibold inline-flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-brand" aria-hidden="true" />
          Authentication
        </h2>
        <p className="text-[13px] text-muted-foreground">
          An admin in your tenant mints an API key from the internal admin UI (Inventory Settings
          → Public API Access). Send it on every request as either header:
        </p>
        <div className="rounded-md bg-slate-900 px-4 py-3 text-xs font-mono overflow-x-auto space-y-1">
          <div className="text-slate-100">X-Api-Key: <span className="text-emerald-400">aqk_prod_...</span></div>
          <div className="text-slate-100">Authorization: Bearer <span className="text-emerald-400">aqk_prod_...</span></div>
        </div>
        <p className="text-[13px] text-muted-foreground">
          Base URL: <code className="bg-muted px-1.5 py-0.5 rounded text-foreground">{baseUrl}</code>
        </p>
        <p className="text-[13px] text-muted-foreground">
          Mutating requests (POST/PATCH) accept an optional{' '}
          <code className="bg-muted px-1.5 py-0.5 rounded text-foreground">Idempotency-Key</code>{' '}
          header — reuse the same value to retry safely without double-writing.
        </p>
      </section>

      {/* ── Endpoints ───────────────────────────────────────────────── */}
      <section className="space-y-4">
        <h2 className="text-[15px] font-semibold text-foreground">Endpoints</h2>
        {endpoints.map((ep) => (
          <EndpointCard key={`${ep.method}-${ep.path}`} endpoint={ep} doc={doc} />
        ))}
      </section>

      {/* ── Product object reference ───────────────────────────────── */}
      {productSchema && (
        <section className="rounded-lg border border-border bg-card p-4 sm:p-5 space-y-3">
          <h2 className="text-[15px] font-semibold text-foreground">Product object</h2>
          <p className="text-[13px] text-muted-foreground">
            The shape returned by every Products endpoint.
          </p>
          <SchemaTable doc={doc} schema={productSchema} />
        </section>
      )}

      {/* ── Errors ──────────────────────────────────────────────────── */}
      {problemSchema && (
        <section className="rounded-lg border border-border bg-card p-4 sm:p-5 space-y-3">
          <h2 className="text-[15px] font-semibold inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-brand" aria-hidden="true" />
            Errors
          </h2>
          <p className="text-[13px] text-muted-foreground">
            Errors use{' '}
            <a
              href="https://www.rfc-editor.org/rfc/rfc9457"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-secondary hover:underline"
            >
              RFC&nbsp;9457 problem details
            </a>
            , with a stable machine-readable <code className="bg-muted px-1 rounded">code</code>{' '}
            for programmatic handling.
          </p>
          <div className="rounded-md bg-slate-900 px-4 py-3 text-xs font-mono overflow-x-auto text-slate-100 whitespace-pre">
{`{
  "type": "https://...",
  "title": "Not found",
  "status": 404,
  "code": "not_found",
  "detail": "..."
}`}
          </div>
        </section>
      )}
    </div>
  )
}

const METHOD_STYLES: Record<string, string> = {
  GET: 'bg-emerald-500/15 text-emerald-400',
  POST: 'bg-amber-500/15 text-amber-400',
  PATCH: 'bg-sky-500/15 text-sky-400',
  PUT: 'bg-sky-500/15 text-sky-400',
  DELETE: 'bg-red-500/15 text-red-400',
}

function EndpointCard({ endpoint, doc }: { endpoint: Endpoint; doc: OpenApiDoc }): JSX.Element {
  const bodySchema = endpoint.requestBodySchemaName
    ? doc.components.schemas[endpoint.requestBodySchemaName]
    : undefined

  return (
    <article className="rounded-lg border border-border bg-card p-4 sm:p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex h-6 min-w-[52px] items-center justify-center rounded-md px-2 text-[11px] font-bold font-mono ${
            METHOD_STYLES[endpoint.method] ?? 'bg-muted text-foreground'
          }`}
        >
          {endpoint.method}
        </span>
        <code className="text-[13px] text-foreground font-mono break-all">{endpoint.path}</code>
        {endpoint.scopes.map((scope) => (
          <span
            key={scope}
            className="h-5 px-1.5 rounded border border-border text-[10px] font-mono text-muted-foreground inline-flex items-center"
          >
            {scope}
          </span>
        ))}
      </div>
      {endpoint.summary && <p className="text-[13px] font-medium text-foreground">{endpoint.summary}</p>}
      {endpoint.description && (
        <p className="text-[13px] text-muted-foreground">{endpoint.description}</p>
      )}

      {endpoint.parameters.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            Parameters
          </div>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-[12px]">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-3 py-1.5">Name</th>
                  <th className="text-left font-medium px-3 py-1.5">In</th>
                  <th className="text-left font-medium px-3 py-1.5">Type</th>
                  <th className="text-left font-medium px-3 py-1.5">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {endpoint.parameters.map((p) => {
                  const { type, notes } = describeProperty(doc, p.schema ?? {})
                  return (
                    <tr key={p.name}>
                      <td className="px-3 py-1.5 font-mono whitespace-nowrap">
                        {p.name}
                        {p.required && <span className="text-destructive"> *</span>}
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{p.in}</td>
                      <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{type}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">
                        {p.description}
                        {notes && <span className="block text-[11px] opacity-80">{notes}</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {bodySchema && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            Request body ({endpoint.requestBodySchemaName})
          </div>
          <SchemaTable doc={doc} schema={bodySchema} />
        </div>
      )}

      {endpoint.responses.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            Responses
          </div>
          <div className="flex flex-wrap gap-2">
            {endpoint.responses.map((r) => (
              <span
                key={r.code}
                className="h-6 px-2 rounded-md border border-border text-[11px] font-mono inline-flex items-center gap-1.5"
                title={r.description}
              >
                <span className={/^2/.test(r.code) ? 'text-emerald-600' : 'text-muted-foreground'}>
                  {r.code}
                </span>
                {r.description && <span className="text-muted-foreground">{r.description}</span>}
              </span>
            ))}
          </div>
        </div>
      )}
    </article>
  )
}

function SchemaTable({ doc, schema }: { doc: OpenApiDoc; schema: OaSchema }): JSX.Element {
  const required = new Set(schema.required ?? [])
  const properties = Object.entries(schema.properties ?? {})
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-[12px]">
        <thead className="bg-muted/40 text-muted-foreground">
          <tr>
            <th className="text-left font-medium px-3 py-1.5">Field</th>
            <th className="text-left font-medium px-3 py-1.5">Type</th>
            <th className="text-left font-medium px-3 py-1.5">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {properties.map(([name, prop]) => {
            const { type, notes } = describeProperty(doc, prop)
            return (
              <tr key={name}>
                <td className="px-3 py-1.5 font-mono whitespace-nowrap">
                  {name}
                  {required.has(name) && <span className="text-destructive"> *</span>}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{type}</td>
                <td className="px-3 py-1.5 text-muted-foreground">
                  {prop.description}
                  {notes && <span className="block text-[11px] opacity-80">{notes}</span>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
